/**
 * Pure layout math for wrapping a row of equal-width world cards/buttons
 * across the available viewport width - shared by `WorldSelectScene`'s
 * world cards and `LeaderboardScene`'s world filter buttons, so both
 * screens wrap on the exact same policy instead of two independently
 * hand-tuned breakpoints. Deliberately framework-free (no Phaser) so it
 * is directly unit-testable without a DOM/Phaser render environment -
 * this repo's vitest config runs under `environment: "node"`, with no
 * `window`, so any module that imports Phaser (even transitively) cannot
 * be imported directly in a test; every existing Scene-behavior test
 * instead reads the Scene file as source text (see
 * `worldsDiscovery.test.ts`). Keeping this specific piece of logic
 * Phaser-free avoids that workaround entirely for the one part of the
 * layout that actually benefits from real assertions.
 */

/**
 * Splits `items` (world cards, or world filter buttons - anything of
 * equal fixed width) into rows based on how many actually fit
 * `viewportWidth` at once - never on `items.length` directly, so this
 * keeps working correctly whether there are 4, 5, or a future 6th world.
 * If every item fits in one row, that's the whole result (the original
 * always-one-row behavior). Otherwise the items are split across the
 * smallest number of rows that fit, distributed as evenly as possible
 * (5 items over 2 rows is always 3+2, never a lonely 4+1) - a balanced
 * wrap reads far better than a greedy fill-then-overflow row.
 */
/** `WorldSelectScene`'s wide-card width/gap - the production values `LeaderboardScene` reuses too (both wrap with the exact same policy). Kept here (not the Scene file) so tests can import them without pulling in Phaser. */
export const CARD_WIDE_WIDTH = 300;
export const CARD_GAP = 24;

export function worldRows<T>(items: readonly T[], viewportWidth: number, itemWidth: number, gap: number): T[][] {
  const maxPerRow = Math.max(1, Math.floor((viewportWidth + gap) / (itemWidth + gap)));
  if (maxPerRow >= items.length) return [[...items]];

  const rowCount = Math.ceil(items.length / maxPerRow);
  const base = Math.floor(items.length / rowCount);
  const extra = items.length % rowCount;

  const rows: T[][] = [];
  let cursor = 0;
  for (let i = 0; i < rowCount; i++) {
    const size = base + (i < extra ? 1 : 0);
    rows.push(items.slice(cursor, cursor + size));
    cursor += size;
  }
  return rows;
}

export interface CardVerticalFit {
  /** Uniform shrink factor actually applied (1 = full size, never > 1). */
  scale: number;
  cardWidth: number;
  cardHeight: number;
  gap: number;
}

/**
 * `worldRows()` (and the stacked single-column case) only ever reasons
 * about how many cards fit `viewportWidth` per row - deciding the 3+2
 * split itself, at full card size, is unaffected by this function. This
 * is the other half: once `rowCount` rows of `cardHeight`-tall cards
 * (`rowCount - 1` gaps between them) are stacked underneath each other,
 * do they actually fit in `usableHeight`? If not, shrink card width,
 * height, AND gap by the same factor until they do (never below
 * `minScale`, so cards never become illegibly small) - never re-decides
 * which cards land in which row. A scene passes the resulting
 * `cardWidth`/`cardHeight` into its existing per-card drawing code
 * unchanged; every proportional detail inside a card (icon radius, font
 * sizes) already derives from that card's own width/height, so nothing
 * else needs to know a shrink happened at all.
 */
export function fitRowsToHeight(
  rowCount: number,
  cardWidth: number,
  cardHeight: number,
  gap: number,
  usableHeight: number,
  minScale = 0.5,
): CardVerticalFit {
  const contentHeight = rowCount * cardHeight + (rowCount - 1) * gap;
  const scale = contentHeight <= usableHeight ? 1 : Math.max(minScale, usableHeight / contentHeight);
  return { scale, cardWidth: cardWidth * scale, cardHeight: cardHeight * scale, gap: gap * scale };
}

export interface StackedScrollLayout {
  /** Never shrunk - always `min(maxCardWidth, viewportWidth - sideMargin)`. */
  cardWidth: number;
  /** Echoed back unchanged - callers pass their own fixed stacked-card height. */
  cardHeight: number;
  /** Total height of all `worldCount` cards plus the gaps between them. */
  contentHeight: number;
  /** How far past `contentHeight - viewportHeight` the list can scroll - 0 when everything already fits. */
  maxScroll: number;
}

/**
 * PR27: the mobile/stacked `WorldSelectScene` layout's own geometry - pure
 * and Phaser-free for the same reason `fitRowsToHeight()` is (see that
 * function's doc). Deliberately the opposite policy from
 * `fitRowsToHeight()`: cards here are NEVER shrunk to fit - every card
 * always renders at its full `cardHeight` (and a `cardWidth` capped only
 * by the viewport's own width, not by vertical space). If the stacked
 * column's total content doesn't fit `viewportHeight`, `maxScroll` is
 * simply how far the scene lets the player scroll instead of shrinking
 * anything.
 */
export function computeStackedScrollLayout(
  worldCount: number,
  viewportWidth: number,
  viewportHeight: number,
  cardHeight: number,
  gap: number,
  maxCardWidth = 360,
  sideMargin = 48,
): StackedScrollLayout {
  const cardWidth = Math.min(maxCardWidth, viewportWidth - sideMargin);
  const contentHeight = worldCount * cardHeight + Math.max(0, worldCount - 1) * gap;
  const maxScroll = Math.max(0, contentHeight - viewportHeight);
  return { cardWidth, cardHeight, contentHeight, maxScroll };
}
